import { request } from './client';
import { isAyricalAccessRole } from '../utils/memberAccess';
import { getApiConfig } from './client';
function asArray(value) {
    return Array.isArray(value) ? value : [];
}
function memberIdentifier(member) {
    return String(member.memberId ||
        member.trello_member_id ||
        member.id ||
        member.user_id ||
        member.supabase_user_id ||
        '');
}
function initialsFromName(name) {
    return name
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() || '')
        .join('');
}
function normalizeAssignedMember(raw) {
    const name = String(raw.name || raw.fullName || raw.full_name || '');
    const employeeId = String(raw.employee_id || raw.id || '');
    return {
        id: employeeId,
        employee_id: employeeId,
        name,
        initials: String(raw.initials || initialsFromName(name)),
        source: typeof raw.source === 'string' ? raw.source : undefined,
        created_at: typeof raw.created_at === 'string' ? raw.created_at : null,
    };
}
// Every role the backend can return. Omitting one silently renders those members
// under the fallback role in the admin UI, so keep this in step with AccessRole.
const ACCESS_ROLES = [
    'viewer',
    'technician',
    'lead_tech',
    'sales',
    'finance',
    'manager',
    'admin',
];
function asRole(value) {
    return ACCESS_ROLES.includes(value) ? value : undefined;
}
function normalizeCard(card) {
    const board = card.board || undefined;
    const list = card.list || undefined;
    const labels = Array.isArray(card.labels)
        ? card.labels.map((label) => {
            const row = (label || {});
            return {
                id: String(row.id || ''),
                name: typeof row.name === 'string' ? row.name : '',
                color: typeof row.color === 'string' ? row.color : '',
            };
        })
        : undefined;
    return {
        id: String(card.id || ''),
        site_id: card.site_id != null ? String(card.site_id) : null,
        site_name: typeof card.site_name === 'string' ? card.site_name : null,
        name: String(card.name || ''),
        desc: typeof card.desc === 'string' ? card.desc : undefined,
        url: typeof card.url === 'string'
            ? card.url
            : (typeof card.shortUrl === 'string' ? card.shortUrl : undefined),
        board_id: typeof card.idBoard === 'string'
            ? card.idBoard
            : (typeof card.board_id === 'string' ? card.board_id : undefined),
        board_name: typeof board?.name === 'string'
            ? board.name
            : (typeof card.board_name === 'string' ? card.board_name : undefined),
        list_name: typeof list?.name === 'string'
            ? list.name
            : (typeof card.list_name === 'string'
                ? card.list_name
                : (typeof card.listName === 'string' ? card.listName : undefined)),
        list_position: typeof card.listPosition === 'number'
            ? card.listPosition
            : (typeof card.list_position === 'number' ? card.list_position : undefined),
        ...(labels ? { labels } : {}),
        is_archived: Boolean(card.closed || card.is_archived),
        is_test: Boolean(card.is_test),
        ticket_count: typeof card.ticketCount === 'number'
            ? card.ticketCount
            : (typeof card.ticket_count === 'number' ? card.ticket_count : 0),
        last_ticket_status: typeof card.last_ticket_status === 'string'
            ? card.last_ticket_status
            : undefined,
    };
}
function normalizeStatusCounts(value) {
    return {
        draft: Number(value?.draft || 0),
        field_review: Number(value?.field_review || 0),
        ready_for_invoice: Number(value?.ready_for_invoice || 0),
        estimated: Number(value?.estimated || 0),
        invoiced: Number(value?.invoiced || 0),
        paid: Number(value?.paid || 0),
        archived: Number(value?.archived || 0),
    };
}
function normalizeAttachmentSize(raw) {
    const candidate = raw.size ?? raw.size_bytes;
    if (candidate === null || candidate === undefined || candidate === '')
        return null;
    const n = Number(candidate);
    if (!Number.isFinite(n) || n <= 0)
        return null;
    return Math.round(n);
}
const ATTACHMENT_TYPE_LABELS = {
    site_photo: 'Site photo',
    receipt: 'Receipt',
    document: 'Document',
};
function normalizeAttachmentType(raw) {
    return raw === 'receipt' || raw === 'document' || raw === 'site_photo'
        ? raw
        : 'site_photo';
}
function normalizeAttachment(raw) {
    const attachmentType = normalizeAttachmentType(raw.attachment_type);
    return {
        id: String(raw.id || ''),
        site_id: raw.site_id != null ? String(raw.site_id) : undefined,
        ticket_id: raw.ticket_id != null ? String(raw.ticket_id) : undefined,
        customer_qb_id: raw.customer_qb_id != null ? String(raw.customer_qb_id) : null,
        vendor_id: raw.vendor_id != null ? String(raw.vendor_id) : null,
        compliance_type: raw.compliance_type === 'tax_exemption'
            || raw.compliance_type === 'resale_certificate'
            || raw.compliance_type === 'w9'
            || raw.compliance_type === 'other'
            ? raw.compliance_type
            : null,
        expires_on: typeof raw.expires_on === 'string' ? raw.expires_on : null,
        filename: String(raw.filename || ''),
        content_type: String(raw.content_type || 'application/octet-stream'),
        size: normalizeAttachmentSize(raw),
        storage_path: String(raw.storage_path || ''),
        url: typeof raw.url === 'string' ? raw.url : undefined,
        uploaded_by: String(raw.uploaded_by || raw.uploaded_by_member_id || ''),
        uploaded_by_name: typeof raw.uploaded_by_name === 'string' ? raw.uploaded_by_name : undefined,
        created_at: String(raw.created_at || raw.uploaded_at || ''),
        trello_attachment_id: raw.trello_attachment_id != null ? String(raw.trello_attachment_id) : null,
        attachment_type: attachmentType,
        attachment_type_label: typeof raw.attachment_type_label === 'string'
            ? raw.attachment_type_label
            : ATTACHMENT_TYPE_LABELS[attachmentType],
    };
}
function normalizeAttachmentsPage(raw, requested = {}) {
    const requestedLimit = requested.limit ?? 50;
    const requestedOffset = requested.offset ?? 0;
    if (Array.isArray(raw)) {
        const rows = raw.map((row) => normalizeAttachment(row));
        return {
            attachments: rows,
            total: rows.length,
            limit: requestedLimit,
            offset: requestedOffset,
            has_more: false,
        };
    }
    const dict = (raw || {});
    const rows = asArray(dict.attachments).map((row) => normalizeAttachment(row));
    const total = typeof dict.total === 'number' ? dict.total : rows.length;
    const limit = typeof dict.limit === 'number' ? dict.limit : requestedLimit;
    const offset = typeof dict.offset === 'number' ? dict.offset : requestedOffset;
    const hasMore = typeof dict.has_more === 'boolean'
        ? dict.has_more
        : offset + rows.length < total;
    return { attachments: rows, total, limit, offset, has_more: hasMore };
}
function normalizeSite(raw) {
    const rawTickets = asArray(raw.tickets);
    const rawAttachments = asArray(raw.attachments);
    const rawCustomFields = asArray(raw.custom_fields);
    return {
        id: String(raw.id || ''),
        card_id: String(raw.card_id || raw.trello_card_id || ''),
        name: String(raw.name || raw.site_name || ''),
        address: typeof raw.address === 'string' ? raw.address : undefined,
        coordinates: typeof raw.coordinates === 'string' ? raw.coordinates : undefined,
        site_notes: typeof raw.site_notes === 'string' ? raw.site_notes : undefined,
        default_customer_id: typeof raw.default_customer_id === 'string' ? raw.default_customer_id : undefined,
        default_customer_name: typeof raw.default_customer_name === 'string'
            ? raw.default_customer_name
            : (typeof raw.default_customer?.display_name === 'string'
                ? raw.default_customer.display_name
                : undefined),
        trello_board_id: typeof raw.trello_board_id === 'string' ? raw.trello_board_id : undefined,
        trello_list_name: typeof raw.trello_list_name === 'string' ? raw.trello_list_name : undefined,
        trello_card_url: typeof raw.trello_card_url === 'string' ? raw.trello_card_url : undefined,
        trello_labels: Array.isArray(raw.trello_labels) ? raw.trello_labels : (typeof raw.trello_labels === 'string' ? JSON.parse(raw.trello_labels) : undefined),
        is_archived: Boolean(raw.is_archived),
        members: asArray(raw.members).map(normalizeAssignedMember),
        last_synced_at: typeof raw.last_synced_at === 'string'
            ? raw.last_synced_at
            : (typeof raw.trello_synced_at === 'string' ? raw.trello_synced_at : undefined),
        created_at: String(raw.created_at || ''),
        updated_at: String(raw.updated_at || ''),
        tickets: rawTickets.length > 0
            ? rawTickets.map((ticket) => ({
                ...ticket,
                work_date: ticket.work_date || ticket.ticket_date,
                description: ticket.description || ticket.description_of_work,
                customer_name: ticket.customer_name || ticket.customer_name_snapshot,
                members: asArray(ticket.members).map(normalizeAssignedMember),
            }))
            : undefined,
        custom_fields: Array.isArray(raw.custom_fields)
            ? rawCustomFields.map((field) => ({
                field_id: String(field.field_id || ''),
                name: String(field.name || ''),
                type: (field.type === 'number'
                    || field.type === 'date'
                    || field.type === 'checkbox'
                    || field.type === 'list') ? field.type : 'text',
                options: Array.isArray(field.options)
                    ? field.options.map((rawOption) => {
                        const option = (rawOption || {});
                        const rawValue = option.value;
                        return {
                            id: String(option.id || ''),
                            value: (typeof rawValue === 'string'
                                || (rawValue != null
                                    && typeof rawValue === 'object'
                                    && !Array.isArray(rawValue))) ? rawValue : null,
                            color: typeof option.color === 'string' ? option.color : null,
                            pos: option.pos == null ? null : Number(option.pos),
                        };
                    })
                    : null,
                value: field.value && typeof field.value === 'object' && !Array.isArray(field.value)
                    ? field.value
                    : null,
                id_value: typeof field.id_value === 'string' ? field.id_value : null,
                display_value: typeof field.display_value === 'string' ? field.display_value : '',
                pos: field.pos == null ? null : Number(field.pos),
                writable: field.writable !== false,
            }))
            : undefined,
        attachments: rawAttachments.length > 0 ? rawAttachments.map(normalizeAttachment) : undefined,
        permissions: raw.permissions && typeof raw.permissions === 'object'
            ? { canEdit: raw.permissions.canEdit !== false }
            : undefined,
    };
}
function normalizeKit(raw) {
    const rawItems = asArray(raw.items);
    const scope = raw.scope === 'team' || raw.scope === 'personal' ? raw.scope : 'org';
    return {
        id: String(raw.id || ''),
        name: String(raw.name || ''),
        description: typeof raw.description === 'string' ? raw.description : undefined,
        scope,
        can_edit: typeof raw.can_edit === 'boolean' ? raw.can_edit : undefined,
        owner_user_id: typeof raw.owner_user_id === 'string' ? raw.owner_user_id : null,
        team_manager_user_id: typeof raw.team_manager_user_id === 'string' ? raw.team_manager_user_id : null,
        items: rawItems.map((item) => {
            // Bundle detail endpoint nests full item data under item.item (includes thumbnail_url)
            const nestedItem = item.item || {};
            const thumbUrl = typeof nestedItem.thumbnail_url === 'string'
                ? nestedItem.thumbnail_url
                : (typeof item.thumbnail_url === 'string' ? item.thumbnail_url : undefined);
            return {
                qb_item_id: String(item.qb_item_id || ''),
                item_name: typeof item.item_name === 'string'
                    ? item.item_name
                    : (typeof item.item_name_snapshot === 'string'
                        ? item.item_name_snapshot
                        : (typeof nestedItem.nickname === 'string'
                            ? nestedItem.nickname
                            : (typeof nestedItem.name === 'string' ? nestedItem.name : undefined))),
                description: typeof item.description === 'string' ? item.description : undefined,
                quantity: Number(item.quantity ?? item.default_quantity ?? 1),
                default_unit_price: item.default_unit_price !== null && item.default_unit_price !== undefined && item.default_unit_price !== ''
                    ? Number(item.default_unit_price)
                    : undefined,
                thumbnail_url: thumbUrl,
            };
        }),
        created_by: typeof raw.created_by === 'string' ? raw.created_by : undefined,
        created_at: String(raw.created_at || ''),
        updated_at: String(raw.updated_at || ''),
    };
}
// ============================================
// Auth
// ============================================
export const auth = {
    getMe: () => request('/auth/me'),
    logout: () => request('/auth/logout', { method: 'POST' }),
    signIn: (email, password) => request('/auth/signin', {
        method: 'POST',
        body: { email, password },
    }),
    signInWithGoogle: (redirectTo, platform) => request('/auth/signin/google', {
        method: 'POST',
        body: { redirectTo, platform },
    }),
    forgotPassword: (email, redirectTo) => request('/auth/forgot-password', {
        method: 'POST',
        body: { email, redirectTo },
    }),
    resetPassword: (code, password) => request('/auth/reset-password', {
        method: 'POST',
        body: { code, password },
    }),
};
// ============================================
// Standalone (cards, dashboard, search)
// ============================================
export const standalone = {
    getDashboard: async () => {
        const data = await request('/standalone/me/dashboard');
        const stats = ('stats' in data
            ? data.stats
            : data);
        return {
            totalCards: Number(stats.totalCards || 0),
            totalTickets: Number(stats.totalTickets || 0),
            ticketsByStatus: normalizeStatusCounts(stats.ticketsByStatus || stats.byStatus),
            totalHours: Number(stats.totalHours || 0),
            totalValue: Number(stats.totalValue || 0),
            recentActivity: asArray(stats.recentActivity),
        };
    },
    getMyTickets: async (params = {}) => {
        const search = new URLSearchParams();
        if (params.status)
            search.set('status', params.status);
        if (params.scope)
            search.set('scope', params.scope);
        const qs = search.toString();
        const data = await request(`/standalone/me/tickets${qs ? `?${qs}` : ''}`);
        if (Array.isArray(data)) {
            return {
                tickets: data.map((ticket) => normalizeTicketResponse(ticket)),
                scope: params.scope || 'me',
                status: params.status || null,
                canSeeFinancials: false,
            };
        }
        const response = data;
        return {
            tickets: asArray(response.tickets).map((ticket) => normalizeTicketResponse(ticket)),
            scope: response.scope === 'team' ? 'team' : 'me',
            status: response.status,
            canSeeFinancials: Boolean(response.canSeeFinancials),
        };
    },
    getMyCards: async (limit = 50) => {
        const data = await request(`/standalone/me/cards?limit=${limit}`);
        const cards = Array.isArray(data) ? data : asArray(data.cards);
        return cards.map((card) => normalizeCard(card));
    },
    getBoards: async () => {
        const data = await request('/boards');
        const rawBoards = Array.isArray(data)
            ? data
            : asArray(data.boards);
        return rawBoards
            .map((raw) => {
            const shortLink = typeof raw.shortLink === 'string'
                ? raw.shortLink
                : (typeof raw.short_link === 'string' ? raw.short_link : undefined);
            return {
                id: typeof raw.trello_board_id === 'string'
                    ? raw.trello_board_id
                    : (typeof raw.id === 'string' ? raw.id : ''),
                name: typeof raw.name === 'string'
                    ? raw.name
                    : (typeof raw.board_name === 'string' ? raw.board_name : ''),
                ...(shortLink ? { shortLink } : {}),
                ...(typeof raw.visible_in_board_view === 'boolean'
                    ? { visibleInBoardView: raw.visible_in_board_view }
                    : {}),
            };
        })
            .filter((board) => Boolean(board.id && board.name));
    },
    getBoardMirror: (boardId) => request(`/boards/${boardId}/mirror`),
    moveTrelloCard: (boardId, cardId, payload) => request(`/boards/${boardId}/trello-cards/${cardId}/move`, {
        method: 'PUT',
        body: payload,
    }),
    moveSiteToList: (boardId, siteId, targetListName) => request(`/boards/${boardId}/cards/${siteId}/move`, {
        method: 'PUT',
        body: { targetListName },
    }),
    syncBoard: (boardId) => request(`/boards/${boardId}/sync`, { method: 'POST' }),
    getCard: async (cardId) => {
        const data = await request(`/standalone/cards/${cardId}`);
        const card = ('card' in data)
            ? data.card
            : data;
        return normalizeCard(card);
    },
    getCardCoordinates: (cardId) => request(`/standalone/cards/${cardId}/coordinates`),
    searchCards: async (query, includeArchived = true, limit = 50) => {
        const data = await request(`/standalone/search/cards?query=${encodeURIComponent(query)}&includeArchived=${includeArchived}&limit=${limit}`);
        const cards = Array.isArray(data) ? data : asArray(data.cards);
        return cards.map((card) => normalizeCard(card));
    },
    // Same endpoint as searchCards, but keeps the ticket-number matches (#846)
    // the route returns alongside the cards. searchCards stays Card[]-only for
    // its existing callers (copy-ticket, PO editor, linked sites).
    searchWorkspace: async (query, includeArchived = true, limit = 50) => {
        const data = await request(`/standalone/search/cards?query=${encodeURIComponent(query)}&includeArchived=${includeArchived}&limit=${limit}`);
        return {
            cards: asArray(data.cards).map((card) => normalizeCard(card)),
            tickets: asArray(data.tickets),
        };
    },
    unarchiveCard: (cardId) => request(`/standalone/cards/${cardId}/unarchive`, {
        method: 'POST',
    }),
};
// ============================================
// Tickets
// ============================================
// Maps UI-friendly field names to the legacy DB column names the backend
// whitelists on write. Backend (`backend/src/routes/tickets.js`) only accepts
// the legacy names (`description_of_work`, `ticket_date`, etc.) and silently
// drops any fields not in its whitelist — so the UI names must be rewritten
// before sending. The legacy key always wins if both happen to be present.
// See issue #43.
function aliasTicketWritePayload(data) {
    const src = data;
    const out = { ...src };
    const mappings = [
        ['description', 'description_of_work'],
        ['work_date', 'ticket_date'],
        ['customer_name', 'customer_name_snapshot'],
        ['bill_to_customer_name', 'bill_to_customer_name_snapshot'],
        ['labor_entries', 'labor'],
        ['material_entries', 'materials'],
        ['member_ids', 'ticket_member_ids'],
    ];
    for (const [uiKey, dbKey] of mappings) {
        if (uiKey in src) {
            // Only promote the UI value to the DB key if the DB key isn't already set
            // (preserves any caller that already used the legacy form).
            if (!(dbKey in src)) {
                out[dbKey] = src[uiKey];
            }
            // Drop the UI-only key so the backend whitelist doesn't ignore a
            // stale duplicate value.
            delete out[uiKey];
        }
    }
    return out;
}
// Mirror DB-only fields onto UI-canonical names on each material row so the
// MaterialsSection price input (which reads `entry.unit_price`) hydrates
// correctly after saves and status transitions. Backend stores
// `unit_price_snapshot` and returns it as-is; without this alias the price
// input rendered blank after every save round-trip (#223). Same shape rule
// for any future material write-side aliasing.
//
// Exported for unit testing. Not part of the package's public API.
export function normalizeMaterialEntry(raw) {
    if (!raw || typeof raw !== 'object')
        return raw;
    return {
        ...raw,
        unit_price: raw.unit_price ?? raw.unit_price_snapshot,
    };
}
// Hydrate UI-friendly aliases from legacy DB column names on every ticket
// read (GET, PUT, POST, transition). Without this, setQueryData caches the
// raw PUT response missing `work_date`/`description`/`customer_name`, the form
// re-hydrates with empty strings, the next save sends `ticket_date: ""`, and
// Postgres 500s with "invalid input syntax for type date". Symptom: save
// indicator stuck at "Unsaved changes" after the first successful save.
// Exported for unit testing. Not part of the package's public API.
export function normalizeTicketResponse(raw) {
    if (!raw || typeof raw !== 'object')
        return raw;
    const cardId = raw.card_id || raw.trello_card_id;
    const trelloCardUrl = raw.trello_card_url || (cardId ? `https://trello.com/c/${cardId}` : undefined);
    const rawMaterials = (raw.material_entries || raw.materials);
    const materialEntries = Array.isArray(rawMaterials)
        ? rawMaterials.map((m) => normalizeMaterialEntry(m))
        : rawMaterials;
    return {
        ...raw,
        card_id: cardId,
        site_id: raw.site_id == null ? undefined : String(raw.site_id),
        trello_card_id: raw.trello_card_id || cardId,
        trello_card_url: trelloCardUrl,
        work_date: raw.work_date || raw.ticket_date,
        description: raw.description || raw.description_of_work,
        customer_name: raw.customer_name || raw.customer_name_snapshot,
        labor_entries: raw.labor_entries || raw.labor,
        material_entries: materialEntries,
        members: asArray(raw.members).map(normalizeAssignedMember),
    };
}
export const tickets = {
    list: async (cardId) => {
        const data = await request(`/cards/${cardId}/tickets`);
        const rows = Array.isArray(data) ? data : asArray(data.tickets);
        return rows.map((t) => normalizeTicketResponse(t));
    },
    get: async (ticketId) => {
        const data = await request(`/tickets/${ticketId}`);
        return normalizeTicketResponse(data);
    },
    /**
     * Confirmed network read, for recovering after an uncertain or committed
     * save. 'no-store' bypasses the HTTP cache, and the web service worker does
     * not route no-store requests, so a failed read rejects instead of
     * answering with an older cached copy.
     */
    getFresh: async (ticketId) => {
        const data = await request(`/tickets/${ticketId}`, { cache: 'no-store' });
        return normalizeTicketResponse(data);
    },
    previewCoordinatesPush: (ticketId) => request(`/tickets/${ticketId}/coordinates/push-to-card`),
    pushCoordinatesToCard: (ticketId) => request(`/tickets/${ticketId}/coordinates/push-to-card`, {
        method: 'POST',
    }),
    create: async (cardId, data) => {
        const res = await request(`/cards/${cardId}/tickets`, {
            method: 'POST',
            body: aliasTicketWritePayload(data),
        });
        return normalizeTicketResponse(res);
    },
    copyTicket: async (ticketId, data) => {
        const res = await request(`/tickets/${ticketId}/copy`, {
            method: 'POST',
            body: {
                target_site_id: data.targetSiteId,
                ...(data.moveTrelloCard !== undefined
                    ? { move_trello_card: data.moveTrelloCard }
                    : {}),
            },
        });
        return normalizeTicketResponse(res);
    },
    /**
     * PUT a ticket. `beforeSend` runs immediately before the request goes out
     * (see RequestOptions.beforeSend). A payload without a version token (an
     * offline snapshot that predates it) is sent as is: the server pins it to
     * the revision it reads (#967), so no separate read precedes the PUT.
     */
    update: async (ticketId, data, signal, beforeSend) => {
        // A version conflict is never retried here. Refetching and reapplying would
        // turn OCC into last-write-wins and could overwrite another user's edit.
        const res = await request(`/tickets/${ticketId}`, {
            method: 'PUT',
            body: aliasTicketWritePayload(data),
            signal,
            beforeSend,
        });
        return normalizeTicketResponse(res);
    },
    listQboSyncCandidates: () => request('/tickets/qbo-sync/candidates'),
    previewQboSync: (data) => request('/tickets/qbo-sync/preview', {
        method: 'POST',
        body: data,
    }),
    executeQboSync: (data) => request('/tickets/qbo-sync/execute', {
        method: 'POST',
        body: data,
    }),
    getQboSyncOperation: (operationId) => request(`/tickets/qbo-sync/operations/${operationId}`),
    reconcileQboSync: (operationId, data) => request(`/tickets/qbo-sync/operations/${operationId}/reconcile`, {
        method: 'POST',
        body: data,
    }),
    cleanupDescription: (data) => request('/tickets/description-cleanup', {
        method: 'POST',
        body: data,
    }),
    transition: async (ticketId, payloadOrTargetStatus, overrideReason, 
    /** Runs right before the request goes out (RequestOptions.beforeSend). */
    beforeSend) => {
        const body = typeof payloadOrTargetStatus === 'string'
            ? { targetStatus: payloadOrTargetStatus, overrideReason }
            : payloadOrTargetStatus;
        const data = await request(`/tickets/${ticketId}/transition`, {
            method: 'POST',
            body,
            ...(beforeSend ? { beforeSend } : {}),
        });
        const unwrapped = ('ticket' in data ? data.ticket : data);
        return normalizeTicketResponse(unwrapped);
    },
    archive: (ticketId) => request(`/tickets/${ticketId}/archive`, {
        method: 'POST',
    }),
    unarchive: async (ticketId) => {
        const response = await request(`/tickets/${ticketId}/unarchive`, {
            method: 'POST',
        });
        return {
            ...response,
            ticket: normalizeTicketResponse(response.ticket),
        };
    },
    /**
     * Recall an already-invoiced ticket (manager/admin only): void its QBO invoice
     * and revert it to 'ready_for_invoice'. The backend hard-refuses a PAID invoice
     * (409 invoice_paid) and is idempotent on an already-voided one. Returns the
     * recall outcome (not the ticket) — callers invalidate to refetch the reverted
     * ticket. `reason` is dropped from the body when omitted.
     *
     * When the QBO invoice bills multiple tickets (a grouped push), the backend
     * refuses with 409 `grouped_invoice` unless `recallGroup` is true — voiding a
     * grouped invoice necessarily recalls every ticket on it, so the caller must
     * confirm that intent. `recall_group` is dropped from the body when omitted.
     */
    recall: (ticketId, reason, recallGroup) => request(`/tickets/${ticketId}/recall`, {
        method: 'POST',
        body: { reason, recall_group: recallGroup },
    }),
    /**
     * Send one or more ready-for-invoice tickets to QBO as a single grouped
     * document. The admin `qbo_invoice_mode` toggle decides estimate vs invoice.
     *
     * `consolidate` carries the per-category choices from the Import popup. Material
     * mode is itemized / by category / one catch-all line; labor and equipment keep
     * boolean toggles. When omitted, the backend uses its legacy defaults.
     *
     * `billToCustomerId` (the shared QBO customer of the grouped tickets) lets the
     * backend remember this shape for that customer and prefill it next time.
     */
    sendToQbo: (ticketIds, consolidate, billToCustomerId, customGroups, docType, 
    // #744: the primary ticket (default first) + the human's per-field header
    // picks. Dropped from the body when undefined; a picked value must be one of
    // the ticket-derived candidates or the push is rejected 422.
    primaryTicketId, headerSelections) => request('/tickets/push-to-qbo-grouped', {
        method: 'POST',
        // `docType: 'estimate'` forces an estimate even in invoice mode. The
        // backend rejects a forced `docType:'invoice'` with 409 when the estimate
        // flag is off, so we only ever ADD the estimate option — never send
        // 'invoice'. Dropped from the body when undefined.
        body: { ticketIds, consolidate, billToCustomerId, customGroups, docType, primaryTicketId, headerSelections },
    }),
    /**
     * Dry-run the grouped push: returns the exact QBO document (lines, totals,
     * doc type) that sendToQbo would create for these tickets with this
     * consolidation shape, plus per-ticket validation — no QBO write, no
     * reservation. Powers the Import popup's live preview.
     *
     * IMPORTANT: pass ticketIds in the SAME order the eventual push will use —
     * customGroups line keys are positional per day section, so a different
     * ticket order between draft and push would address different lines (the
     * server's fingerprint check would then fail the push closed).
     */
    draftQboImport: (ticketIds, consolidate, customGroups, docType, 
    // #744: mirror the push's primary/header inputs so the preview reflects the
    // exact header the push will use and returns the candidate pickers.
    primaryTicketId, headerSelections) => request('/tickets/draft-qbo-invoice', {
        method: 'POST',
        // Mirror the push override so the preview shows the estimate the push will
        // create. Dropped from the body when undefined.
        body: { ticketIds, grouped: true, consolidate, customGroups, docType, primaryTicketId, headerSelections },
    }),
    /**
     * Same-origin QBO-rendered estimate PDF download URL. The browser fetches it
     * directly (anchor href / new tab); the route streams application/pdf as an
     * attachment. 404 if the estimate is not ticket-linked. Gate: financials_view.
     * Mirrors `salesApi.documentPdfUrl` for the ticket-linked estimate case.
     */
    estimatePdfUrl: (qbEstimateId) => `/api/tickets/estimates/${encodeURIComponent(qbEstimateId)}/pdf`,
    /**
     * Email the QBO-rendered estimate to the customer through QuickBooks. `email`
     * is optional — the backend defaults to the bill-to customer's mirror email.
     * On success the ticket's `estimate_sent_at` is set (chip flips to
     * "Estimate sent"). 400 `recipient_required` when no valid recipient exists.
     * Gate: financials_view. Callers invalidate ticket/queue/site keys.
     */
    sendEstimate: (qbEstimateId, email) => request(`/tickets/estimates/${encodeURIComponent(qbEstimateId)}/send`, {
        method: 'POST',
        body: { email },
    }),
    /**
     * Convert a ticket-linked estimate into a new linked QBO invoice (one-click).
     * The whole estimated group moves to 'invoiced'. `customerPoId` optionally
     * stamps the customer PO. Error codes: estimate_already_converted (409),
     * convert_validation_failed (422 with details.validationResults),
     * estimate_missing_in_qbo (404), estimate_status_unverified (503),
     * qbo_convert_failed (502). Gate: convert_estimate OR manage_invoices.
     */
    convertEstimate: (qbEstimateId, ticketQboDocumentId, customerPoId) => request(`/tickets/estimates/${encodeURIComponent(qbEstimateId)}/convert`, {
        method: 'POST',
        body: {
            customer_po_id: customerPoId,
            ticket_qbo_document_id: ticketQboDocumentId,
        },
    }),
    /**
     * Last-used consolidation shape for a bill-to customer, to prefill the Import
     * popup. Returns null config when nothing has been saved for them yet.
     */
    getConsolidationPrefs: (customerId) => request(`/tickets/consolidation-prefs?customerId=${encodeURIComponent(customerId)}`),
    getAttachments: async (ticketId, options = {}) => {
        const params = new URLSearchParams();
        if (options.limit != null)
            params.set('limit', String(options.limit));
        if (options.offset != null)
            params.set('offset', String(options.offset));
        const qs = params.toString();
        const path = `/tickets/${ticketId}/attachments${qs ? `?${qs}` : ''}`;
        const data = await request(path);
        return normalizeAttachmentsPage(data, options);
    },
    uploadAttachment: async (ticketId, formData) => {
        const data = await request(`/tickets/${ticketId}/attachments`, {
            method: 'POST',
            body: formData,
        });
        return normalizeAttachment(data);
    },
    deleteAttachment: (attachmentId) => request(`/attachments/${attachmentId}`, {
        method: 'DELETE',
    }),
    updateAttachment: async (attachmentId, data) => {
        const res = await request(`/attachments/${attachmentId}`, {
            method: 'PATCH',
            body: data,
        });
        return normalizeAttachment(res);
    },
    deleteTicket: (ticketId) => request(`/tickets/${ticketId}`, {
        method: 'DELETE',
    }),
    permanentDelete: (ticketId) => request(`/tickets/${ticketId}/permanent`, {
        method: 'DELETE',
    }),
    syncTrello: (ticketId) => request(`/tickets/${ticketId}/sync-trello`, {
        method: 'POST',
    }),
    validateForPush: (ticketIds) => request('/tickets/validate-for-push', {
        method: 'POST',
        body: { ticketIds },
    }),
    getAuditTrail: async (ticketId) => {
        const data = await request(`/tickets/${ticketId}/audit`);
        if (Array.isArray(data))
            return data;
        return asArray(data.events);
    },
    listComments: async (ticketId) => {
        const data = await request(`/tickets/${ticketId}/comments`);
        if (Array.isArray(data))
            return data;
        return asArray(data.comments);
    },
    createComment: async (ticketId, body) => {
        const data = await request(`/tickets/${ticketId}/comments`, {
            method: 'POST',
            body: { body },
        });
        const unwrapped = data.comment ?? data;
        return unwrapped;
    },
    updateComment: async (ticketId, commentId, body) => {
        const data = await request(`/tickets/${ticketId}/comments/${commentId}`, {
            method: 'PUT',
            body: { body },
        });
        return (data.comment ?? data);
    },
    deleteComment: async (ticketId, commentId) => {
        await request(`/tickets/${ticketId}/comments/${commentId}`, {
            method: 'DELETE',
        });
    },
    /**
     * Archive the Trello card linked to a ticket (manager+, invoiced status only).
     * On first call, the backend returns 409 with siblingCount when other open tickets
     * share the card. Pass { confirm: true } to proceed despite siblings.
     */
    archiveCard: (ticketId, opts = {}) => request(`/tickets/${ticketId}/archive-card`, { method: 'POST', body: opts.confirm ? { confirm: true } : {} }),
    /**
     * Clear the billing-review flag on a ticket (manager+).
     */
    resolveBilling: (ticketId) => request(`/tickets/${ticketId}/resolve-billing`, { method: 'PUT' }),
    updateIntermediarySubmission: async (ticketId, data) => {
        const res = await request(`/tickets/${ticketId}/intermediary-submission`, { method: 'PUT', body: data });
        return {
            ...res,
            ticket: res.ticket ? normalizeTicketResponse(res.ticket) : undefined,
        };
    },
    markInvoiced: async (ticketId, data) => {
        const res = await request(`/tickets/${ticketId}/mark-invoiced`, { method: 'PUT', body: data });
        return {
            ...res,
            ticket: res.ticket ? normalizeTicketResponse(res.ticket) : undefined,
        };
    },
    bulkMarkInvoiced: (ticketIds, invoiceNumber, allowShared) => request('/tickets/bulk-mark-invoiced', {
        method: 'POST',
        body: {
            ticket_ids: ticketIds,
            invoice_number: invoiceNumber,
            allow_shared: allowShared,
        },
    }),
    /**
     * Refresh the QBO paid status for a ticket's invoice (manager+).
     *
     * Pulls the latest invoice state from QuickBooks via qb-get-invoice and
     * stamps `field_tickets.paid_at` if QBO reports a zero balance. Read-only
     * with respect to QBO — never mutates QuickBooks.
     */
    refreshPaidStatus: (ticketId) => request(`/tickets/${ticketId}/refresh-paid-status`, { method: 'POST' }),
};
// ============================================
// Catalog
// ============================================
export const catalog = {
    searchCustomers: async (query) => {
        const data = await request(`/catalog/customers?query=${encodeURIComponent(query || '')}`);
        if (Array.isArray(data))
            return data;
        const favorites = asArray(data.favorites);
        const results = asArray(data.results);
        return [...favorites, ...results];
    },
    createCustomer: async (data) => {
        const res = await request('/catalog/customers', {
            method: 'POST',
            body: data,
        });
        return ('customer' in res ? res.customer : res);
    },
    searchItems: async (query, options) => {
        const params = new URLSearchParams({ query: query || '' });
        if (options?.limit)
            params.set('limit', String(options.limit));
        const data = await request(`/catalog/items?${params}`);
        if (Array.isArray(data))
            return data;
        const favorites = asArray(data.favorites);
        const results = asArray(data.results);
        return [...favorites, ...results];
    },
    /** Full active catalog for the client-side index (instant search + offline). */
    listAllItems: async () => {
        const data = await request('/catalog/items/all');
        if (Array.isArray(data))
            return data;
        return asArray(data.items);
    },
    getKits: async () => {
        const data = await request('/catalog/bundles');
        const kits = Array.isArray(data) ? data : asArray(data.kits);
        return kits.map(normalizeKit);
    },
    getKitDetail: async (kitId) => {
        const data = await request(`/catalog/bundles/${kitId}`);
        return normalizeKit(data);
    },
    getEmployees: async (options) => {
        const params = options?.wirelessOnly || options?.commsOnly
            ? `?${options?.commsOnly ? 'comms_only=true' : 'wireless_only=true'}`
            : '';
        const data = await request(`/catalog/employees${params}`);
        const rows = Array.isArray(data) ? data : asArray(data.employees);
        return rows.map((emp) => ({
            id: String(emp.id || ''),
            name: String(emp.full_name || emp.name || ''),
            initials: initialsFromName(String(emp.full_name || emp.name || '')),
            department: typeof emp.department === 'string' ? emp.department : undefined,
            default_labor_type: emp.default_labor_type === 'field' || emp.default_labor_type === 'pm' || emp.default_labor_type === 'after_hours'
                ? emp.default_labor_type
                : null,
            is_active: Boolean(emp.is_active ?? (emp.status === 'Active')),
        }));
    },
    getStats: () => request('/catalog/items/stats'),
    updateNickname: (qbItemId, nickname) => request(`/catalog/items/${qbItemId}/nickname`, {
        method: 'PUT',
        body: { nickname },
    }),
    updateInternalNotes: (qbItemId, internalNotes) => request(`/catalog/items/${qbItemId}/internal-notes`, {
        method: 'PUT',
        body: { internal_notes: internalNotes },
    }),
    // Alturex-private item data (vendor/internal SKU, cost, margin, notes). Read
    // is Sales/Manager/Admin via sales_view (#577; cost/margin additionally
    // redacted for non-finance); write is Manager/Sales.
    getPrivate: (qbItemId) => request(`/catalog/items/${qbItemId}/private`),
    updatePrivate: (qbItemId, data) => request(`/catalog/items/${qbItemId}/private`, {
        method: 'PUT',
        body: data,
    }),
    updateItem: (qbItemId, data) => request(`/catalog/items/${qbItemId}`, {
        method: 'PUT',
        body: data,
    }),
    // Manager+ only (financials_view). Realm-scoped item price timeline; each row
    // distinguishes app-side vs QuickBooks-side (source='sync') changes.
    getPriceHistory: (qbItemId) => request(`/catalog/items/${qbItemId}/price-history`),
    // Explicit, audited write of the item's unit price to QuickBooks (source of
    // truth). Manager+ only, blocked during impersonation. Returns the refreshed
    // local catalog item plus the raw QBO item.
    updateQboPrice: (qbItemId, unitPrice) => request(`/catalog/items/${qbItemId}/qbo-price`, {
        method: 'PUT',
        body: { unit_price: unitPrice },
    }),
    uploadThumbnail: async (qbItemId, formData) => request(`/catalog/items/${qbItemId}/thumbnail`, { method: 'POST', body: formData }),
    deleteThumbnail: (qbItemId) => request(`/catalog/items/${qbItemId}/thumbnail`, {
        method: 'DELETE',
    }),
};
// ============================================
// Team Favorites (managed by any member since #577, readable by all)
// ============================================
export const teamFavorites = {
    get: async () => {
        const data = await request('/catalog/team-favorites');
        return Array.isArray(data) ? data : asArray(data.favorites);
    },
    add: (qbItemId) => request(`/catalog/team-favorites/${qbItemId}`, {
        method: 'POST',
    }),
    remove: (qbItemId) => request(`/catalog/team-favorites/${qbItemId}`, {
        method: 'DELETE',
    }),
};
// ============================================
// Team (Manager only)
// ============================================
function nullableCount(value) {
    if (value === null || value === undefined)
        return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}
export const team = {
    list: async () => {
        const data = await request('/standalone/me/team');
        const raw = Array.isArray(data) ? data : asArray(data.teamMembers);
        return raw.map((member) => ({
            memberId: memberIdentifier(member),
            username: String(member.username || member.trello_username || ''),
            fullName: String(member.fullName || member.trello_full_name || ''),
            employeeId: typeof member.employee_id === 'string' ? member.employee_id : undefined,
            // null means "Trello unavailable" — keep it distinct from a real zero
            // so the UI can render an em-dash instead of a false 0.
            cardCount: nullableCount(member.cardCount ?? member.card_count),
            ticketCount: nullableCount(member.ticketCount ?? member.ticket_count),
        }));
    },
    dashboard: async () => {
        const data = await request('/standalone/me/team-dashboard');
        if ('aggregateStats' in data) {
            const response = data;
            const aggregateStats = response.aggregateStats || {};
            const memberStats = asArray(response.memberStats);
            return {
                totalTechs: Number(response.teamSize || memberStats.length || 0),
                totalCards: memberStats.reduce((sum, m) => sum + Number(m.totalCards || 0), 0),
                totalTickets: Number(aggregateStats.totalTickets || 0),
                totalHours: Number(aggregateStats.totalHours || 0),
                totalValue: Number(aggregateStats.totalValue || 0),
                members: memberStats.map((member) => ({
                    memberId: memberIdentifier(member),
                    username: String(member.username || ''),
                    fullName: String(member.fullName || ''),
                    ticketCount: Number(member.totalTickets || 0),
                    cardCount: Number(member.totalCards || 0),
                })),
            };
        }
        return data;
    },
    /**
     * Manager dashboard payload — team table, flat open team tickets, and the
     * manager's own open tickets. Hits the same endpoint as `dashboard()` but
     * extracts the redesign-era fields (`team`, `team_open_tickets`, `my_cards`)
     * and normalizes the embedded ticket rows so KanbanTicketCard can consume
     * them directly.
     */
    managerOverview: async () => {
        const data = await request('/standalone/me/team-dashboard');
        const teamRows = asArray(data.team).map((row) => ({
            user_id: String(row.user_id || ''),
            full_name: String(row.full_name || ''),
            email: typeof row.email === 'string' ? row.email : null,
            avatar_url: typeof row.avatar_url === 'string' ? row.avatar_url : null,
            open_ticket_count: Number(row.open_ticket_count || 0),
            hours_this_period: Number(row.hours_this_period || 0),
            value_this_period: Number(row.value_this_period || 0),
        }));
        const teamOpen = asArray(data.team_open_tickets).map((t) => normalizeTicketResponse(t));
        const teamDrafts = asArray(data.team_draft_tickets).map((t) => normalizeTicketResponse(t));
        const myCards = asArray(data.my_cards).map((t) => normalizeTicketResponse(t));
        const period = data.period;
        return {
            team: teamRows,
            team_open_tickets: teamOpen,
            team_draft_tickets: teamDrafts,
            my_cards: myCards,
            period: {
                start: typeof period?.start === 'string' ? period.start : '',
                end: typeof period?.end === 'string' ? period.end : '',
            },
        };
    },
    /**
     * Correction Loop weekly report — per-tech reviewer-kickback counts over a
     * rolling window (default 7 days). Manager/admin only; backend 403s others.
     */
    corrections: async (params = {}) => {
        const qs = params.days != null ? `?days=${encodeURIComponent(String(params.days))}` : '';
        const data = await request(`/standalone/me/team-corrections${qs}`);
        const corrections = asArray(data.corrections).map((row) => ({
            user_id: String(row.user_id || ''),
            full_name: String(row.full_name || ''),
            kickback_count: Number(row.kickback_count || 0),
            recent: asArray(row.recent).map((r) => ({
                ticket_id: String(r.ticket_id || ''),
                note: typeof r.note === 'string' ? r.note : null,
                created_at: String(r.created_at || ''),
            })),
        }));
        return {
            since_days: Number(data.since_days || 7),
            corrections,
        };
    },
    memberCards: async (techMemberId) => {
        const data = await request(`/standalone/team/${techMemberId}/cards`);
        const cards = Array.isArray(data) ? data : asArray(data.cards);
        return cards.map((card) => normalizeCard(card));
    },
    memberDashboard: async (techMemberId) => {
        const data = await request(`/standalone/team/${techMemberId}/dashboard`);
        const stats = ('stats' in data
            ? data.stats
            : data);
        return {
            totalCards: Number(stats.totalCards || 0),
            totalTickets: Number(stats.totalTickets || 0),
            ticketsByStatus: normalizeStatusCounts(stats.ticketsByStatus || stats.byStatus),
            totalHours: Number(stats.totalHours || 0),
            totalValue: Number(stats.totalValue || 0),
        };
    },
};
// ============================================
// Admin
// ============================================
function asAyricalRole(value) {
    return isAyricalAccessRole(value) ? value : null;
}
function memberStatusChange(path, memberId, reason) {
    return request(`/admin/members/${encodeURIComponent(memberId)}/${path}`, {
        method: 'PUT',
        body: { reason },
    });
}
export const admin = {
    // #2103: open to member managers (Admin/Manager/Sales). Non-admins get only
    // the members Ayrical lets them manage; `can_manage` marks each record.
    getMembers: async () => {
        const data = await request('/admin/members');
        const rows = Array.isArray(data) ? data : asArray(data.members);
        return rows.map((member) => {
            const manager = member.manager;
            return {
                id: String(member.id || ''),
                memberId: String(member.trello_member_id || member.id || ''),
                username: String(member.trello_username || member.username || ''),
                fullName: String(member.display_name || member.trello_full_name || member.fullName || ''),
                email: typeof member.email === 'string' ? member.email : undefined,
                employeeId: typeof member.employee_id === 'string' ? member.employee_id : undefined,
                employeeName: typeof member.employee?.full_name === 'string'
                    ? member.employee.full_name
                    : (typeof member.employeeName === 'string' ? member.employeeName : undefined),
                hasTicketProfile: typeof member.has_ticket_profile === 'boolean'
                    ? member.has_ticket_profile
                    : (typeof member.hasTicketProfile === 'boolean' ? member.hasTicketProfile : true),
                role: asRole(member.role),
                capabilities: typeof member.capabilities === 'object' && member.capabilities !== null
                    ? member.capabilities
                    : undefined,
                isLeadTech: Boolean(member.is_lead_tech || member.isLeadTech),
                isManager: Boolean(member.is_manager || member.isManager),
                isAdmin: Boolean(member.is_admin || member.isAdmin),
                managerMemberId: typeof member.manager_member_id === 'string'
                    ? member.manager_member_id
                    : (typeof member.managerMemberId === 'string' ? member.managerMemberId : undefined),
                managerName: typeof manager?.display_name === 'string'
                    ? manager.display_name
                    : (typeof manager?.trello_full_name === 'string'
                        ? manager.trello_full_name
                        : (typeof member.managerName === 'string' ? member.managerName : undefined)),
                region: typeof member.region === 'string' ? member.region : undefined,
                defaultReviewShopKey: typeof member.default_review_shop_key === 'string'
                    ? member.default_review_shop_key
                    : (typeof member.defaultReviewShopKey === 'string'
                        ? member.defaultReviewShopKey
                        : null),
                status: typeof member.status === 'string' ? member.status : undefined,
                membershipId: typeof member.membership_id === 'string' ? member.membership_id : null,
                accessRole: asAyricalRole(member.access_role),
                canManage: member.can_manage === true,
            };
        });
    },
    getManagers: async () => {
        const data = await request('/admin/managers');
        const rows = Array.isArray(data) ? data : asArray(data.managers);
        return rows.map((manager) => ({
            id: String(manager.id || ''),
            memberId: String(manager.trello_member_id || manager.id || ''),
            fullName: String(manager.display_name || manager.trello_full_name || manager.fullName || ''),
        }));
    },
    getPendingMembers: async () => {
        const data = await request('/admin/pending-members');
        const rows = asArray(data.members);
        return rows.map((member) => ({
            id: String(member.id || ''),
            userId: typeof member.user_id === 'string' ? member.user_id : null,
            membershipId: typeof member.membership_id === 'string' ? member.membership_id : null,
            email: String(member.email || ''),
            displayName: String(member.display_name || member.email || ''),
            createdAt: String(member.created_at || member.invited_at || ''),
            invitePending: Boolean(member.invite_pending),
            accessRole: asAyricalRole(member.access_role),
            invitedAt: typeof member.invited_at === 'string' ? member.invited_at : null,
            tokenExpiresAt: typeof member.token_expires_at === 'string' ? member.token_expires_at : null,
            canManage: member.can_manage === true,
        }));
    },
    /** Roles the signed-in user may grant. */
    getMemberAccess: async () => {
        const data = await request('/admin/member-access');
        const roles = Array.isArray(data.grantable_roles) ? data.grantable_roles : [];
        return {
            grantableRoles: roles.filter(isAyricalAccessRole),
            manageUrl: typeof data.manage_url === 'string' ? data.manage_url : '',
        };
    },
    inviteMember: async ({ email, accessRole }) => {
        const data = await request('/admin/members/invite', {
            method: 'POST',
            body: { email, access_role: accessRole },
        });
        return {
            memberId: typeof data.member_id === 'string' ? data.member_id : null,
            email: typeof data.email === 'string' ? data.email : email,
            accessRole: asAyricalRole(data.access_role),
            emailSent: data.email_sent === true,
            emailError: typeof data.email_error === 'string' ? data.email_error : null,
        };
    },
    changeMemberAccessRole: (memberId, accessRole) => request(`/admin/members/${encodeURIComponent(memberId)}/access-role`, {
        method: 'PUT',
        body: { access_role: accessRole },
    }),
    // Ayrical requires a reason for every status change.
    suspendMember: (memberId, reason) => memberStatusChange('suspend', memberId, reason),
    reactivateMember: (memberId, reason) => memberStatusChange('reactivate', memberId, reason),
    resendInvitation: (membershipId) => request(`/admin/invitations/${encodeURIComponent(membershipId)}/resend`, {
        method: 'POST',
    }),
    revokeInvitation: (membershipId) => request(`/admin/invitations/${encodeURIComponent(membershipId)}`, {
        method: 'DELETE',
    }),
    updateMember: (memberId, data) => request(`/admin/members/${memberId}`, {
        method: 'PUT',
        body: data,
    }),
    assignManager: (memberId, managerMemberId) => request(`/admin/members/${memberId}/assign-manager`, {
        method: 'PUT',
        body: { managerMemberId },
    }),
    // Labor-picker roster (Admin > Employees, #668).
    getEmployees: async () => {
        const data = await request('/admin/employees');
        return asArray(data.employees);
    },
    createEmployee: async (data) => {
        const res = await request('/admin/employees', {
            method: 'POST',
            body: data,
        });
        return res.employee;
    },
    updateEmployee: async (employeeId, data) => {
        const res = await request(`/admin/employees/${encodeURIComponent(employeeId)}`, { method: 'PUT', body: data });
        return res.employee;
    },
    getSettings: async () => {
        const data = await request('/admin/settings');
        return asArray(data.settings).map((s) => ({
            key: String(s.key || ''),
            label: String(s.label || ''),
            description: String(s.description || ''),
            enabled: Boolean(s.enabled),
            danger: Boolean(s.danger),
            configured: Boolean(s.configured),
        }));
    },
    setSetting: (key, enabled) => request(`/admin/settings/${encodeURIComponent(key)}`, {
        method: 'PUT',
        body: { enabled },
    }),
    getCustomerTaxRules: async () => {
        const data = await request('/admin/customer-tax-rules');
        return asArray(data.rules);
    },
    createCustomerTaxRule: async (input) => {
        const data = await request('/admin/customer-tax-rules', {
            method: 'POST',
            body: input,
        });
        return data.rule;
    },
    updateCustomerTaxRule: async (ruleId, input) => {
        const data = await request(`/admin/customer-tax-rules/${encodeURIComponent(ruleId)}`, { method: 'PUT', body: input });
        return data.rule;
    },
    deleteCustomerTaxRule: (ruleId) => request(`/admin/customer-tax-rules/${encodeURIComponent(ruleId)}`, { method: 'DELETE' }),
    // Manager-reachable read/write of the org-wide estimate/invoice switch only.
    // Backed by the same `qbo_invoice_mode` flag as getSettings/setSetting but
    // gated on `manage_billing_mode` so Managers (who can't reach Admin) can flip
    // it from the Review Queue. See routes/admin.js /billing-mode.
    getBillingMode: async () => {
        const { setting } = await request('/admin/billing-mode');
        return setting;
    },
    setBillingMode: (enabled) => request('/admin/billing-mode', {
        method: 'PUT',
        body: { enabled },
    }),
    // #455 — Admin > Audit tab reads. All three are raw pass-throughs of the
    // backend's paginated { events|sessions, total, limit, offset } shape.
    /** Admin-wide ticket event trail (GET /api/admin/audit-log). */
    getAuditLog: (options = {}) => {
        const params = new URLSearchParams();
        if (options.limit != null)
            params.set('limit', String(options.limit));
        if (options.offset != null)
            params.set('offset', String(options.offset));
        const qs = params.toString();
        return request(`/admin/audit-log${qs ? `?${qs}` : ''}`);
    },
    /** member/po/customer_po/qbo mutation trail (GET /api/admin/admin-actions-log). */
    getAdminActionsLog: (options = {}) => {
        const params = new URLSearchParams();
        if (options.limit != null)
            params.set('limit', String(options.limit));
        if (options.offset != null)
            params.set('offset', String(options.offset));
        if (options.action)
            params.set('action', options.action);
        const qs = params.toString();
        return request(`/admin/admin-actions-log${qs ? `?${qs}` : ''}`);
    },
    /** "Act as user" session trail (GET /api/admin/impersonation-log). */
    getImpersonationLog: (options = {}) => {
        const params = new URLSearchParams();
        if (options.limit != null)
            params.set('limit', String(options.limit));
        if (options.offset != null)
            params.set('offset', String(options.offset));
        const qs = params.toString();
        return request(`/admin/impersonation-log${qs ? `?${qs}` : ''}`);
    },
    getKits: async () => {
        const data = await request('/catalog/bundles');
        const kits = Array.isArray(data) ? data : asArray(data.kits);
        return kits.map(normalizeKit);
    },
    createKit: async (data) => normalizeKit(await request('/catalog/bundles', {
        method: 'POST',
        body: {
            ...data,
            items: data.items.map((item) => ({
                qb_item_id: item.qb_item_id,
                item_name_snapshot: item.item_name_snapshot,
                description: item.description,
                default_quantity: item.quantity,
                default_unit_price: item.default_unit_price,
            })),
        },
    })),
    updateKit: async (kitId, data) => normalizeKit(await request(`/catalog/bundles/${kitId}`, {
        method: 'PUT',
        body: {
            ...data,
            ...(data.items
                ? {
                    items: data.items.map((item) => ({
                        qb_item_id: item.qb_item_id,
                        item_name_snapshot: item.item_name_snapshot,
                        description: item.description,
                        default_quantity: item.quantity,
                        default_unit_price: item.default_unit_price,
                    })),
                }
                : {}),
        },
    })),
    deleteKit: (kitId) => request(`/catalog/bundles/${kitId}`, { method: 'DELETE' }),
};
// ============================================
// AR / Recovery Reporting (financials-gated)
// ============================================
function reportRangeQuery(range) {
    const params = new URLSearchParams();
    if (range?.from)
        params.set('from', range.from);
    if (range?.to)
        params.set('to', range.to);
    const qs = params.toString();
    return qs ? `?${qs}` : '';
}
export const reports = {
    arLag: (range) => request(`/reports/ar-lag${reportRangeQuery(range)}`),
    techBillable: (range) => request(`/reports/tech-billable${reportRangeQuery(range)}`),
    laborCaptureWireless: (body, signal) => request('/reports/labor-capture-wireless', {
        method: 'POST',
        body,
        signal,
        timeoutMs: 65_000,
    }),
    listLaborCaptureWorkPresets: () => request('/reports/labor-capture-wireless/presets'),
    createLaborCaptureWorkPreset: (payload) => request('/reports/labor-capture-wireless/presets', {
        method: 'POST',
        body: payload,
    }),
    deleteLaborCaptureWorkPreset: (id) => request(`/reports/labor-capture-wireless/presets/${id}`, {
        method: 'DELETE',
    }),
    // ── Report builder (#873) ──────────────────────────────────────────────
    // The registry is served, not hardcoded, so the builder can never offer a
    // metric or grouping the runner would reject.
    metadata: () => request('/reports/metadata'),
    run: (config) => request('/reports/run', { method: 'POST', body: config }),
    listSaved: () => request('/reports/saved'),
    createSaved: (payload) => request('/reports/saved', { method: 'POST', body: payload }),
    updateSaved: (id, patch) => request(`/reports/saved/${id}`, { method: 'PATCH', body: patch }),
    deleteSaved: (id) => request(`/reports/saved/${id}`, { method: 'DELETE' }),
};
// ============================================
// Impersonation
// ============================================
export const impersonation = {
    start: (targetUserId, reason, ttlMinutes) => request('/admin/impersonate', {
        method: 'POST',
        body: { targetUserId, reason, ...(ttlMinutes != null ? { ttlMinutes } : {}) },
    }),
    stop: () => request('/admin/impersonate/stop', {
        method: 'POST',
    }),
    active: () => request('/admin/impersonate/active'),
};
// ============================================
// Geocoding
// ============================================
export const geocoding = {
    geocode: (coordinates) => request('/geocode', {
        method: 'POST',
        body: { coordinates },
    }),
    validate: (coordinates) => request('/geocode/validate', {
        method: 'POST',
        body: { coordinates },
    }),
};
// ============================================
// Sites
// ============================================
export const sites = {
    get: async (siteId) => {
        const data = await request(`/sites/${siteId}`);
        const site = ('site' in data)
            ? data.site
            : data;
        return normalizeSite(site || {});
    },
    getByCardId: async (cardId) => {
        const data = await request(`/standalone/cards/${cardId}/site`);
        return data.site;
    },
    purgeArchivedTickets: (siteId) => request(`/sites/${siteId}/tickets/purge-archived`, {
        method: 'POST',
    }),
    sync: async (siteId) => {
        const data = await request(`/sites/${siteId}/sync`, { method: 'POST' });
        const response = data;
        const rawSite = response.site || response;
        const site = normalizeSite(rawSite);
        const attachments = asArray(response.attachments).map(normalizeAttachment);
        return {
            ...site,
            synced: Boolean(response.synced),
            updatedAt: typeof response.updatedAt === 'string' ? response.updatedAt : site.last_synced_at,
            site,
            attachmentsSynced: Number(response.attachmentsSynced || 0),
            attachments,
        };
    },
    updateNotes: async (siteId, notes) => {
        const data = await request(`/sites/${siteId}/notes`, {
            method: 'PUT',
            body: { notes },
        });
        const response = data;
        const rawSite = response.site || response;
        return normalizeSite(rawSite);
    },
    updateMembers: async (siteId, employeeIds) => {
        const data = await request(`/sites/${siteId}/members`, {
            method: 'PUT',
            body: { employee_ids: employeeIds },
        });
        return asArray(data.members).map(normalizeAssignedMember);
    },
    updateCustomField: (siteId, fieldId, value) => request(`/sites/${siteId}/custom-fields/${encodeURIComponent(fieldId)}`, {
        method: 'PUT',
        body: { value },
    }),
    getLabels: (siteId) => request(`/sites/${siteId}/labels/available`),
    addLabel: (siteId, input) => request(`/sites/${siteId}/labels`, {
        method: 'POST',
        body: input,
    }),
    removeLabel: (siteId, labelId) => request(`/sites/${siteId}/labels/${encodeURIComponent(labelId)}`, {
        method: 'DELETE',
    }),
    getNotesHistory: async (siteId) => {
        const data = await request(`/sites/${siteId}/notes/history`);
        const rows = Array.isArray(data) ? data : asArray(data.history);
        return rows.map((row) => {
            const entry = row;
            return {
                id: String(entry.id || ''),
                site_id: String(entry.site_id || ''),
                notes: String(entry.notes || entry.content || ''),
                changed_by: String(entry.changed_by || entry.edited_by_member_id || ''),
                changed_by_name: typeof entry.changed_by_name === 'string' ? entry.changed_by_name : undefined,
                created_at: String(entry.created_at || entry.edited_at || ''),
            };
        });
    },
    getAttachments: async (siteId, options = {}) => {
        const params = new URLSearchParams();
        if (options.limit != null)
            params.set('limit', String(options.limit));
        if (options.offset != null)
            params.set('offset', String(options.offset));
        const qs = params.toString();
        const path = `/sites/${siteId}/attachments${qs ? `?${qs}` : ''}`;
        const data = await request(path);
        return normalizeAttachmentsPage(data, options);
    },
    uploadAttachment: async (siteId, formData) => {
        // Use request but with FormData body (Content-Type auto-set)
        const data = await request(`/sites/${siteId}/attachments`, {
            method: 'POST',
            body: formData,
        });
        return normalizeAttachment(data);
    },
    deleteAttachment: (attachmentId) => request(`/attachments/${attachmentId}`, {
        method: 'DELETE',
    }),
    updateAttachment: async (attachmentId, data) => {
        const res = await request(`/attachments/${attachmentId}`, {
            method: 'PATCH',
            body: data,
        });
        return normalizeAttachment(res);
    },
    getSyncStatus: (siteId) => request(`/sites/${siteId}/sync-status`),
};
// ============================================
// Site Comments (Wave 4 / M2)
// ============================================
/**
 * REST surface for `public.site_comments`. Mirrors `tickets.listComments` /
 * `createComment` / `deleteComment` but with an additional PATCH for edits
 * and accepting both array-and-wrapped JSON shapes from the API.
 */
export const siteComments = {
    list: async (siteId) => {
        const data = await request(`/sites/${siteId}/comments`);
        if (Array.isArray(data))
            return data;
        return asArray(data.comments);
    },
    create: async (siteId, body) => {
        const data = await request(`/sites/${siteId}/comments`, {
            method: 'POST',
            body: { body },
        });
        return (data.comment ?? data);
    },
    update: async (siteId, commentId, body) => {
        const data = await request(`/sites/${siteId}/comments/${commentId}`, {
            method: 'PATCH',
            body: { body },
        });
        return (data.comment ?? data);
    },
    remove: async (siteId, commentId) => {
        await request(`/sites/${siteId}/comments/${commentId}`, {
            method: 'DELETE',
        });
    },
};
// ============================================
// Site Checklists (Trello mirror)
// ============================================
/**
 * REST surface for `public.site_checklists` + `public.site_checklist_items`.
 * Backend webhook + sync paths populate these tables. Item state changes go
 * through Trello first, then the backend updates the local mirror.
 */
export const siteChecklists = {
    list: async (siteId) => {
        const data = await request(`/sites/${siteId}/checklists`);
        if (Array.isArray(data))
            return data;
        return asArray(data.checklists);
    },
    updateItemState: async (siteId, itemId, state) => {
        const data = await request(`/sites/${siteId}/checklist-items/${itemId}`, {
            method: 'PUT',
            body: { state },
        });
        return (data.item ?? data);
    },
};
// ============================================
// Site Notes (Wave 4 / M1 — multi-note stack)
// ============================================
/**
 * REST surface for `public.site_notes`. Backed by backend/src/routes/site-notes.js.
 * Notes are ordered by `pinned DESC, position ASC, created_at ASC` server-side;
 * the M4 frontend re-applies that ordering defensively. System notes
 * (`is_system=true`) are read-only on these endpoints — the backend rejects
 * edits with 403.
 */
export const siteNotes = {
    list: async (siteId, options = {}) => {
        const params = new URLSearchParams();
        if (options.includeArchived)
            params.set('includeArchived', 'true');
        const qs = params.toString();
        const path = `/sites/${siteId}/notes${qs ? `?${qs}` : ''}`;
        const data = await request(path);
        if (Array.isArray(data))
            return data;
        return asArray(data.notes);
    },
    create: async (siteId, payload) => {
        const data = await request(`/sites/${siteId}/notes`, {
            method: 'POST',
            body: payload,
        });
        return (data.note ?? data);
    },
    update: async (siteId, noteId, payload) => {
        const data = await request(`/sites/${siteId}/notes/${noteId}`, {
            method: 'PUT',
            body: payload,
        });
        return (data.note ?? data);
    },
    archive: async (siteId, noteId) => {
        const data = await request(`/sites/${siteId}/notes/${noteId}/archive`, { method: 'POST' });
        return (data.note ?? data);
    },
    remove: async (siteId, noteId) => {
        await request(`/sites/${siteId}/notes/${noteId}`, {
            method: 'DELETE',
        });
    },
    reorder: async (siteId, items) => {
        const data = await request(`/sites/${siteId}/notes/reorder`, {
            method: 'PUT',
            body: { items },
        });
        return { updated: Number(data?.updated || 0) };
    },
    listVersions: async (siteId, noteId) => {
        const data = await request(`/sites/${siteId}/notes/${noteId}/versions`);
        if (Array.isArray(data))
            return data;
        return asArray(data.versions);
    },
};
// ============================================
// Review Queue (Manager only)
// ============================================
export const review = {
    getQueue: async (options) => {
        const params = new URLSearchParams();
        if (options?.includeImports) {
            params.set('include_imports', 'true');
        }
        if (options?.shopKey) {
            params.set('shop', options.shopKey);
        }
        if (options?.listId) {
            params.set('list_id', options.listId);
        }
        const qs = params.toString();
        const path = qs ? `/review/queue?${qs}` : '/review/queue';
        const data = await request(path);
        if (Array.isArray(data))
            return { tickets: data, lists: [], metadata: undefined };
        return {
            tickets: data.tickets || [],
            lists: data.lists || [],
            metadata: data.metadata
                ? {
                    invoicedLingerDays: Number(data.metadata.invoiced_linger_days) || 0,
                    shops: (data.metadata.shops || []).filter((shop) => typeof shop?.key === 'string' && typeof shop?.label === 'string'),
                }
                : undefined,
        };
    },
    /**
     * Actionable review-queue count for the nav badge. Hits the lightweight
     * count-only endpoint (HEAD + exact count) instead of pulling the full queue.
     */
    getQueueCount: async (options) => {
        const params = new URLSearchParams();
        if (options?.shopKey) {
            params.set('shop', options.shopKey);
        }
        const qs = params.toString();
        const path = qs ? `/tickets/needs-review/count?${qs}` : '/tickets/needs-review/count';
        const data = await request(path);
        return data.count ?? 0;
    },
    correctMaterial: (materialId, data) => request(`/review/materials/${materialId}/correct`, {
        method: 'PUT',
        body: data,
    }),
    approveMaterial: (materialId) => request(`/review/materials/${materialId}/approve`, {
        method: 'PUT',
    }),
    bulkApprove: (ticketIds) => request('/review/tickets/bulk-approve', {
        method: 'POST',
        body: { ticket_ids: ticketIds },
    }),
};
export const sales = {
    list: () => request('/sales'),
    /** Paginated, filterable estimate/invoice history from the QBO mirror cache. */
    listDocuments: (params) => {
        const qs = new URLSearchParams();
        qs.set('type', params.type);
        if (params.status)
            qs.set('status', params.status);
        if (params.q)
            qs.set('q', params.q);
        if (params.customerId)
            qs.set('customer_id', params.customerId);
        if (params.dateFrom)
            qs.set('date_from', params.dateFrom);
        if (params.dateTo)
            qs.set('date_to', params.dateTo);
        if (params.amountMin != null)
            qs.set('amount_min', String(params.amountMin));
        if (params.amountMax != null)
            qs.set('amount_max', String(params.amountMax));
        if (params.limit != null)
            qs.set('limit', String(params.limit));
        if (params.offset != null)
            qs.set('offset', String(params.offset));
        return request(`/sales/documents?${qs.toString()}`);
    },
    /**
     * A single estimate/invoice with parsed QBO line items + linked tickets.
     * `live: true` fetches straight from QuickBooks (falls back to cache on error;
     * the returned doc's `source` says which). Default reads the mirror cache.
     */
    getDocument: (type, id, opts = {}) => request(`/sales/documents/${type}/${encodeURIComponent(id)}${opts.live ? '?live=1' : ''}`),
    /** Same-origin QBO-rendered PDF download URL. Browser fetches it directly. */
    documentPdfUrl: (type, id) => `/api/sales/documents/${type}/${encodeURIComponent(id)}/pdf`,
    /**
     * Email the QBO-rendered estimate/invoice to `sendTo` through QuickBooks.
     * QBO renders and delivers its own PDF; blocked while impersonating.
     */
    sendDocumentEmail: (type, id, sendTo) => request(`/sales/documents/${type}/${encodeURIComponent(id)}/send`, { method: 'POST', body: { send_to: sendTo } }),
    /** Manager/Admin QBO write: void an invoice that is not ticket-linked. */
    voidInvoice: (qbInvoiceId) => request(`/sales/documents/invoice/${encodeURIComponent(qbInvoiceId)}/void`, { method: 'POST' }),
    /** Manager/Admin QBO write: delete an estimate that has not been invoiced/used. */
    deleteEstimate: (qbEstimateId) => request(`/sales/documents/estimate/${encodeURIComponent(qbEstimateId)}/delete`, { method: 'POST' }),
    /** Alturex-local notes + links staged on a doc (never synced to QBO). */
    getMeta: (type, id) => request(`/sales/documents/${type}/${encodeURIComponent(id)}/meta`),
    updateMeta: (type, id, data) => request(`/sales/documents/${type}/${encodeURIComponent(id)}/meta`, {
        method: 'PUT',
        body: data,
    }),
    // ── Sales doc attachments (files / photos) — never sent to QBO ───────────
    getAttachments: async (type, id, options = {}) => {
        const params = new URLSearchParams();
        if (options.limit != null)
            params.set('limit', String(options.limit));
        if (options.offset != null)
            params.set('offset', String(options.offset));
        const qs = params.toString();
        const path = `/sales/documents/${type}/${encodeURIComponent(id)}/attachments${qs ? `?${qs}` : ''}`;
        const data = await request(path);
        return normalizeAttachmentsPage(data, options);
    },
    uploadAttachment: async (type, id, formData) => {
        const data = await request(`/sales/documents/${type}/${encodeURIComponent(id)}/attachments`, { method: 'POST', body: formData });
        return normalizeAttachment(data);
    },
    deleteAttachment: (type, id, attachmentId) => requestNoContent(`/sales/documents/${type}/${encodeURIComponent(id)}/attachments/${attachmentId}`, 'DELETE'),
    // ── Estimate builder (Phase 3, flag-gated) ──────────────────────────────
    /** Whether the estimate builder is enabled (not itself behind the flag). */
    builderStatus: () => request('/sales/builder-status'),
    listDrafts: () => request('/sales/drafts'),
    createDraft: () => request('/sales/drafts', { method: 'POST' }),
    /** Create an edit-mode draft from a posted estimate, read live (revise → replace its lines). */
    createDraftFromEstimate: (qbEstimateId) => request(`/sales/drafts/from-estimate/${encodeURIComponent(qbEstimateId)}`, { method: 'POST' }),
    getDraft: (id) => request(`/sales/drafts/${id}`),
    saveDraft: (id, data, signal) => request(`/sales/drafts/${id}`, { method: 'PUT', body: data, signal }),
    deleteDraft: (id) => requestNoContent(`/sales/drafts/${id}`, 'DELETE'),
    /**
     * Post a draft to QuickBooks as a real estimate (idempotent). For an edit-mode
     * draft this replaces the lines of the existing estimate; if it changed in QBO
     * since the edit opened, the request 409s with `{ conflict, code, current }`.
     * To overwrite that version, pass `overwriteSyncToken: current.base_sync_token`
     * (sent as `{ force: true, sync_token }`): the write is pinned to the version
     * the user saw, so a newer change conflicts again.
     */
    postDraft: (id, options = {}) => {
        const body = {};
        if (options.overwriteSyncToken) {
            body.force = true;
            body.sync_token = options.overwriteSyncToken;
        }
        if (options.customerPoId)
            body.customer_po_id = options.customerPoId;
        return request(`/sales/drafts/${id}/post`, {
            method: 'POST',
            ...(Object.keys(body).length > 0 ? { body } : {}),
        });
    },
    /**
     * Resolve an edit conflict the non-destructive way: pull the CURRENT live
     * estimate into the edit draft and re-baseline its SyncToken, so the user can
     * re-apply their edits on top of the concurrent change and re-post cleanly.
     */
    reloadDraftFromEstimate: (id) => request(`/sales/drafts/${id}/reload-from-estimate`, { method: 'POST' }),
};
/**
 * Invoice builder (Phase 4, flag-gated, Manager/Admin only). Mirrors the estimate
 * builder, but a draft has a mode: 'create' posts a new QBO invoice, 'edit' updates
 * an existing one (incl. issued) via qb-update-invoice. Drafts can be seeded blank,
 * from an existing invoice (edit), or from an estimate (convert).
 */
export const invoices = {
    /** Whether the invoice builder is enabled (not itself behind the flag). */
    builderStatus: () => request('/invoices/builder-status'),
    listDrafts: () => request('/invoices/drafts'),
    /** Seed a blank create-mode draft. */
    createDraft: () => request('/invoices/drafts', { method: 'POST' }),
    /** Seed an edit-mode draft prefilled from an existing QBO invoice. */
    createFromInvoice: (qbInvoiceId) => request(`/invoices/drafts/from-invoice/${encodeURIComponent(qbInvoiceId)}`, { method: 'POST' }),
    /** Seed a create-mode draft prefilled from an estimate's lines (convert). */
    createFromEstimate: (qbEstimateId) => request(`/invoices/drafts/from-estimate/${encodeURIComponent(qbEstimateId)}`, { method: 'POST' }),
    getDraft: (id) => request(`/invoices/drafts/${id}`),
    saveDraft: (id, data) => request(`/invoices/drafts/${id}`, { method: 'PUT', body: data }),
    deleteDraft: (id) => requestNoContent(`/invoices/drafts/${id}`, 'DELETE'),
    /**
     * Post the draft to QuickBooks — creates or updates the invoice (idempotent on
     * create). An edit draft whose invoice changed in QBO since the edit opened
     * 409s with `{ conflict, code, current }`. To overwrite that version, pass
     * `overwriteSyncToken: current.base_sync_token` (sent as `{ force: true,
     * sync_token }`): the write is pinned to the version the user saw.
     */
    postDraft: (id, options = {}) => {
        const body = {};
        if (options.overwriteSyncToken) {
            body.force = true;
            body.sync_token = options.overwriteSyncToken;
        }
        if (options.customerPoId)
            body.customer_po_id = options.customerPoId;
        return request(`/invoices/drafts/${id}/post`, {
            method: 'POST',
            ...(Object.keys(body).length > 0 ? { body } : {}),
        });
    },
    /**
     * Resolve an edit conflict the non-destructive way: re-read the live invoice
     * into the edit draft (lines, dates, memos, version) so the user can re-apply
     * their edits on top and re-post.
     */
    reloadDraftFromInvoice: (id) => request(`/invoices/drafts/${id}/reload-from-invoice`, { method: 'POST' }),
};
export const customerPos = {
    list: (params) => {
        const qs = new URLSearchParams();
        qs.set('qb_customer_id', params.qbCustomerId);
        if (params.includeInactive)
            qs.set('include_inactive', '1');
        const suffix = qs.toString();
        return request(`/customer-pos?${suffix}`);
    },
    create: (data) => request('/customer-pos', { method: 'POST', body: data }),
    update: (id, data) => request(`/customer-pos/${encodeURIComponent(id)}`, { method: 'PUT', body: data }),
    setStatus: (id, status) => request(`/customer-pos/${encodeURIComponent(id)}/status`, {
        method: 'POST',
        body: { status },
    }),
    retryBaserow: (id) => request(`/customer-pos/${encodeURIComponent(id)}/retry-baserow`, { method: 'POST' }),
};
export const customers = {
    /** Paginated, searchable customer directory (Sales › Customers tab). */
    list: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.q)
            qs.set('q', params.q);
        if (params.activeOnly)
            qs.set('active_only', '1');
        if (params.limit != null)
            qs.set('limit', String(params.limit));
        if (params.offset != null)
            qs.set('offset', String(params.offset));
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        return request(`/customers${suffix}`);
    },
    /** Customer 360 — record + estimates + invoices + tickets + totals + local notes. */
    getSummary: (qbCustomerId) => request(`/customers/${encodeURIComponent(qbCustomerId)}`),
    getAttachments: async (qbCustomerId, options = {}) => {
        const qs = new URLSearchParams();
        if (options.limit != null)
            qs.set('limit', String(options.limit));
        if (options.offset != null)
            qs.set('offset', String(options.offset));
        const suffix = qs.toString();
        const data = await request(`/customers/${encodeURIComponent(qbCustomerId)}/attachments${suffix ? `?${suffix}` : ''}`);
        return normalizeAttachmentsPage(data, options);
    },
    uploadAttachment: async (qbCustomerId, formData) => {
        const data = await request(`/customers/${encodeURIComponent(qbCustomerId)}/attachments`, { method: 'POST', body: formData });
        return normalizeAttachment(data);
    },
    /** Upsert Alturex-local notes/tags (never synced to QBO). */
    updateNotes: (qbCustomerId, data) => request(`/customers/${encodeURIComponent(qbCustomerId)}/notes`, {
        method: 'PUT',
        body: data,
    }),
    /** Upsert the full Alturex-local CRM field set (never synced to QBO). */
    updateMeta: (qbCustomerId, data) => request(`/customers/${encodeURIComponent(qbCustomerId)}/meta`, {
        method: 'PUT',
        body: data,
    }),
    /** Assignable account owners for the CRM owner picker. */
    accountOwners: () => request(`/customers/account-owners`),
    /** Write QBO identity fields BACK to QuickBooks (Manager/Admin). Real QBO mutation. */
    updateQbo: (qbCustomerId, data) => request(`/customers/${encodeURIComponent(qbCustomerId)}/qbo`, {
        method: 'PUT',
        body: data,
    }),
    /** Make QBO's global Taxable flag match the customer's all-state app rule. */
    updateTaxStatusQbo: (qbCustomerId) => request(`/customers/${encodeURIComponent(qbCustomerId)}/tax-status/qbo`, { method: 'POST' }),
    /** Read-only Ayrow CRM read-back: activity feed + contacts + open follow-ups. */
    activity: (qbCustomerId) => request(`/customers/${encodeURIComponent(qbCustomerId)}/activity`),
};
/**
 * Customer note stack (#872). Ordered `pinned DESC, position ASC, created_at ASC`
 * server-side; the hooks re-apply that defensively. The pin toggle is `update`
 * with `{ pinned }` — there is no separate pin endpoint. NOTE: the legacy
 * single-field `customers.updateNotes` (PUT .../notes) is a DIFFERENT endpoint
 * writing `customer_local_meta.notes`; it is kept for API back-compat and must
 * not be confused with this stack.
 */
export const customerNotes = {
    list: async (qbCustomerId, options = {}) => {
        const params = new URLSearchParams();
        if (options.includeArchived)
            params.set('includeArchived', 'true');
        const qs = params.toString();
        const data = await request(`/customers/${encodeURIComponent(qbCustomerId)}/notes${qs ? `?${qs}` : ''}`);
        return asArray(data?.notes);
    },
    create: async (qbCustomerId, payload) => {
        const data = await request(`/customers/${encodeURIComponent(qbCustomerId)}/notes`, { method: 'POST', body: payload });
        return data.note;
    },
    update: async (qbCustomerId, noteId, payload) => {
        const data = await request(`/customers/${encodeURIComponent(qbCustomerId)}/notes/${encodeURIComponent(noteId)}`, { method: 'PUT', body: payload });
        return data.note;
    },
    archive: async (qbCustomerId, noteId) => {
        const data = await request(`/customers/${encodeURIComponent(qbCustomerId)}/notes/${encodeURIComponent(noteId)}/archive`, { method: 'POST' });
        return data.note;
    },
    remove: async (qbCustomerId, noteId) => {
        await request(`/customers/${encodeURIComponent(qbCustomerId)}/notes/${encodeURIComponent(noteId)}`, { method: 'DELETE' });
    },
    reorder: async (qbCustomerId, items) => {
        await request(`/customers/${encodeURIComponent(qbCustomerId)}/notes/reorder`, { method: 'PUT', body: { items } });
    },
    listVersions: async (qbCustomerId, noteId) => {
        const data = await request(`/customers/${encodeURIComponent(qbCustomerId)}/notes/${encodeURIComponent(noteId)}/versions`);
        return asArray(data?.versions);
    },
};
/** Generic attachment metadata/delete operations retain their route-level auth. */
export const attachmentRecords = {
    updateCompliance: async (attachmentId, data) => {
        const response = await request(`/attachments/${encodeURIComponent(attachmentId)}`, {
            method: 'PATCH',
            body: data,
        });
        return normalizeAttachment(response);
    },
    /**
     * Display-only rename (#872). PATCH accepts `filename` on its own; the
     * storage_path is never touched, so the stored object keeps its original key
     * and existing URLs stay valid.
     */
    rename: async (attachmentId, filename) => {
        const response = await request(`/attachments/${encodeURIComponent(attachmentId)}`, {
            method: 'PATCH',
            body: { filename },
        });
        return normalizeAttachment(response);
    },
    delete: (attachmentId) => request(`/attachments/${encodeURIComponent(attachmentId)}`, { method: 'DELETE' }),
};
// ============================================
// Notifications (W3-D)
// ============================================
/**
 * `request()` calls `response.json()` unconditionally, which throws on 204
 * No Content. The notifications backend returns 204 for `markRead` / `delete`,
 * so we need a tiny fetch wrapper that tolerates empty-body responses while
 * still re-using the configured baseUrl + auth handling.
 */
async function requestNoContent(endpoint, method) {
    const config = getApiConfig();
    const url = `${config.baseUrl}/api${endpoint}`;
    const headers = {};
    if (config.getToken) {
        const token = await config.getToken();
        if (token)
            headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(url, {
        method,
        headers,
        credentials: config.getToken ? 'omit' : 'include',
    });
    if (!response.ok) {
        let message = `Request failed with status ${response.status}`;
        try {
            const data = await response.json();
            message = data?.message || data?.error || message;
        }
        catch {
            /* not JSON, keep default */
        }
        const err = { status: response.status, message, data: null };
        throw err;
    }
}
export const notifications = {
    list: async (params = {}) => {
        const search = new URLSearchParams();
        if (params.cursor)
            search.set('cursor', params.cursor);
        if (params.limit != null)
            search.set('limit', String(params.limit));
        if (params.unreadOnly)
            search.set('unreadOnly', 'true');
        const qs = search.toString();
        const data = await request(`/notifications${qs ? `?${qs}` : ''}`);
        const items = Array.isArray(data.items)
            ? (data.items)
            : [];
        const nextCursor = typeof data.nextCursor === 'string'
            ? (data.nextCursor)
            : null;
        return { items, nextCursor };
    },
    unreadCount: async () => {
        const data = await request('/notifications/unread-count');
        return Number(data?.count || 0);
    },
    markRead: (id) => requestNoContent(`/notifications/${id}/read`, 'PATCH'),
    markAllRead: async () => {
        const data = await request('/notifications/mark-all-read', {
            method: 'POST',
        });
        return { updated: Number(data?.updated || 0) };
    },
    remove: (id) => requestNoContent(`/notifications/${id}`, 'DELETE'),
};
// ============================================
// Feedback (v1 in-app feedback capture)
// ============================================
export const feedback = {
    /** Submit feedback (any authenticated member). Returns the new row's id. */
    submit: (data) => request('/feedback', {
        method: 'POST',
        body: data,
    }),
    /** List feedback, newest-first, capped at 100 rows (manager/admin only). */
    list: async (status) => {
        const qs = status ? `?status=${encodeURIComponent(status)}` : '';
        const data = await request(`/feedback${qs}`);
        return asArray(data.feedback);
    },
    /** Update status/triage_note/github_issue_number (manager/admin only). */
    update: (id, data) => request(`/feedback/${id}`, {
        method: 'PUT',
        body: data,
    }),
};
// ============================================
// Chat
// ============================================
export const chat = {
    status: () => request('/chat/status'),
};
// ============================================
// Vendors + Purchase Orders (#546 Phase 1)
// ============================================
/**
 * Vendor management (po_manage-gated). The lightweight picker list used by the
 * catalog item drawer stays on `catalog` (`GET /catalog/vendors`); these are the
 * full-detail management endpoints.
 */
export const vendors = {
    list: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.q)
            qs.set('q', params.q);
        if (params.includeInactive)
            qs.set('include_inactive', '1');
        const suffix = qs.toString();
        return request(`/purchase-orders/vendors${suffix ? `?${suffix}` : ''}`);
    },
    create: (data) => request('/purchase-orders/vendors', { method: 'POST', body: data }),
    update: (id, data) => request(`/purchase-orders/vendors/${id}`, { method: 'PUT', body: data }),
    getAttachments: async (vendorKey, options = {}) => {
        const qs = new URLSearchParams();
        if (options.limit != null)
            qs.set('limit', String(options.limit));
        if (options.offset != null)
            qs.set('offset', String(options.offset));
        const suffix = qs.toString();
        const data = await request(`/purchase-orders/vendors/${encodeURIComponent(vendorKey)}/attachments${suffix ? `?${suffix}` : ''}`);
        return normalizeAttachmentsPage(data, options);
    },
    uploadAttachment: async (vendorKey, formData) => {
        const data = await request(`/purchase-orders/vendors/${encodeURIComponent(vendorKey)}/attachments`, { method: 'POST', body: formData });
        return normalizeAttachment(data);
    },
};
/**
 * Purchase orders (po_manage-gated). Send remains local-only until the backend
 * po_qbo_create flag routes it through the QBO create orchestration.
 */
export const purchaseOrders = {
    list: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.status)
            qs.set('status', params.status);
        if (params.vendorId)
            qs.set('vendor_id', params.vendorId);
        if (params.q)
            qs.set('q', params.q);
        if (params.includeArchived)
            qs.set('include_archived', 'true');
        if (params.limit != null)
            qs.set('limit', String(params.limit));
        if (params.offset != null)
            qs.set('offset', String(params.offset));
        const suffix = qs.toString();
        return request(`/purchase-orders${suffix ? `?${suffix}` : ''}`);
    },
    get: (id) => request(`/purchase-orders/${id}`),
    qboList: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.q)
            qs.set('q', params.q);
        if (params.status)
            qs.set('status', params.status);
        if (params.limit != null)
            qs.set('limit', String(params.limit));
        if (params.offset != null)
            qs.set('offset', String(params.offset));
        const suffix = qs.toString();
        return request(`/purchase-orders/qbo${suffix ? `?${suffix}` : ''}`);
    },
    qboSync: () => request('/purchase-orders/qbo/sync', { method: 'POST' }),
    qboRefresh: (qbPoId) => request(`/purchase-orders/qbo/${encodeURIComponent(qbPoId)}/refresh`, { method: 'POST' }),
    listSiteLinksForQboPo: (qbPoId) => request(`/purchase-orders/qbo/${encodeURIComponent(qbPoId)}/site-links`),
    listSiteLinksForAppPo: (id) => request(`/purchase-orders/${encodeURIComponent(id)}/site-links`),
    itemVendorCosts: (qbItemId) => request(`/purchase-orders/items/${encodeURIComponent(qbItemId)}/vendor-costs`),
    create: (data) => request('/purchase-orders', { method: 'POST', body: data }),
    /** Draft-only; the server 409s on sent/cancelled POs. Replaces lines wholesale. */
    update: (id, data) => request(`/purchase-orders/${id}`, { method: 'PUT', body: data }),
    /** QBO-first edit for an already-synced, open PO. */
    qboEdit: (id, data) => request(`/purchase-orders/${id}/qbo-edit`, { method: 'POST', body: data }),
    /** draft → sent: stamps sent_at; lines become immutable. */
    send: (id) => request(`/purchase-orders/${id}/send`, { method: 'POST' }),
    syncPurchaseOrderQboNote: (id) => request(`/purchase-orders/${id}/sync-qbo-note`, { method: 'POST' }),
    /** draft/sent → cancelled. `reason` is required and appended to notes. */
    cancel: (id, reason) => request(`/purchase-orders/${id}/cancel`, {
        method: 'POST',
        body: { reason },
    }),
    /**
     * Receive goods against a PO (#546 Phase 2). inventory_receive-gated so every
     * role can receive; the response is cost-redacted for non-finance callers.
     */
    receive: (id, data) => request(`/purchase-orders/${id}/receipts`, { method: 'POST', body: data }),
    /** Receipt history for a PO (po_manage). */
    getReceipts: (id) => request(`/purchase-orders/${id}/receipts`),
    /** Short-close (sent|partially_received → closed). `reason` required. */
    close: (id, reason) => request(`/purchase-orders/${id}/close`, {
        method: 'POST',
        body: { reason },
    }),
    /** Drafts only — sent POs are a paper trail and can only be cancelled. */
    delete: (id) => requestNoContent(`/purchase-orders/${id}`, 'DELETE'),
    // ── Attachments (vendor quotes, order confirmations) ──────────────────────
    getAttachments: async (id, options = {}) => {
        const qs = new URLSearchParams();
        if (options.limit != null)
            qs.set('limit', String(options.limit));
        if (options.offset != null)
            qs.set('offset', String(options.offset));
        const suffix = qs.toString();
        const data = await request(`/purchase-orders/${id}/attachments${suffix ? `?${suffix}` : ''}`);
        return normalizeAttachmentsPage(data, options);
    },
    uploadAttachment: async (id, formData) => {
        const data = await request(`/purchase-orders/${id}/attachments`, {
            method: 'POST',
            body: formData,
        });
        return normalizeAttachment(data);
    },
    deleteAttachment: (id, attachmentId) => requestNoContent(`/purchase-orders/${id}/attachments/${attachmentId}`, 'DELETE'),
};
export const poSiteLinks = {
    listForSite: (siteId) => request(`/sites/${encodeURIComponent(String(siteId))}/po-links`),
    create: (siteId, data) => request(`/sites/${encodeURIComponent(String(siteId))}/po-links`, {
        method: 'POST',
        body: data,
    }),
    updateNote: (id, note) => request(`/po-links/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: { note },
    }),
    delete: (id) => requestNoContent(`/po-links/${encodeURIComponent(id)}`, 'DELETE'),
};
/**
 * Inventory (#546 Phase 2). On-hand quantities are open to every role
 * (inventory_view); the item ledger returns cost only to finance roles (server
 * redacts); cost history is finance-only; track-inventory toggle is manager+.
 * App-owned — nothing here touches QuickBooks.
 */
export const inventory = {
    onHand: (businessUnitId) => {
        const suffix = businessUnitId ? `?business_unit_id=${encodeURIComponent(businessUnitId)}` : '';
        return request(`/inventory/on-hand${suffix}`);
    },
    locations: (businessUnitId) => {
        const suffix = businessUnitId ? `?business_unit_id=${encodeURIComponent(businessUnitId)}` : '';
        return request(`/inventory/locations${suffix}`);
    },
    ledger: (qbItemId, businessUnitId) => {
        const suffix = businessUnitId ? `?business_unit_id=${encodeURIComponent(businessUnitId)}` : '';
        return request(`/inventory/items/${qbItemId}/ledger${suffix}`);
    },
    costHistory: (qbItemId, businessUnitId) => {
        const suffix = businessUnitId ? `?business_unit_id=${encodeURIComponent(businessUnitId)}` : '';
        return request(`/inventory/items/${qbItemId}/cost-history${suffix}`);
    },
    setTrackInventory: (qbItemId, tracked, businessUnitId) => request(`/inventory/items/${qbItemId}/track-inventory`, { method: 'PUT', body: { tracked, ...(businessUnitId ? { business_unit_id: businessUnitId } : {}) } }),
};
// ============================================
// Compliance register (#1011)
// ============================================
/** Batch commits upload up to 100 MB of files; give them room. */
const COMPLIANCE_UPLOAD_TIMEOUT_MS = 180_000;
function complianceRecordsQuery(params) {
    const qs = new URLSearchParams();
    if (params.reviewStatus)
        qs.set('review_status', params.reviewStatus);
    if (params.subjectKind)
        qs.set('subject_kind', params.subjectKind);
    if (params.employeeId)
        qs.set('employee_id', params.employeeId);
    if (params.vehicleId)
        qs.set('vehicle_id', params.vehicleId);
    if (params.businessUnitId)
        qs.set('business_unit_id', params.businessUnitId);
    if (params.documentTypeKey)
        qs.set('document_type_key', params.documentTypeKey);
    if (params.expiringWithinDays != null)
        qs.set('expiring_within_days', String(params.expiringWithinDays));
    if (params.includeArchived)
        qs.set('include_archived', 'true');
    if (params.limit != null)
        qs.set('limit', String(params.limit));
    if (params.offset != null)
        qs.set('offset', String(params.offset));
    if (params.sort)
        qs.set('sort', params.sort);
    const suffix = qs.toString();
    return suffix ? `?${suffix}` : '';
}
/**
 * Register reads and UI writes. Every route 404s while the
 * `compliance_register` flag is off, and every read is scoped in SQL: a
 * missing row means "not visible to you", never "does not exist".
 */
export const compliance = {
    scope: () => request('/compliance/scope'),
    documentTypes: () => request('/compliance/document-types'),
    employees: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.businessUnitId)
            qs.set('business_unit_id', params.businessUnitId);
        if (params.q)
            qs.set('q', params.q);
        if (params.includeInactive)
            qs.set('include_inactive', 'true');
        const suffix = qs.toString();
        return request(`/compliance/employees${suffix ? `?${suffix}` : ''}`);
    },
    records: (params = {}, options = {}) => request(`/compliance/records${complianceRecordsQuery(params)}`, {
        signal: options.signal,
    }),
    record: (recordId) => request(`/compliance/records/${encodeURIComponent(recordId)}`),
    matrix: (businessUnitId) => request(`/compliance/matrix${businessUnitId ? `?business_unit_id=${encodeURIComponent(businessUnitId)}` : ''}`),
    /** Inline bytes (images render in <img>, PDFs in the browser viewer). */
    fileUrl: (fileId) => `/api/compliance/files/${encodeURIComponent(fileId)}/file`,
    /** Attachment download. Restricted files are logged by the server. */
    fileDownloadUrl: (fileId) => `/api/compliance/files/${encodeURIComponent(fileId)}/download`,
    /** Verify / reject / edit. A stale `expected_revision` is a 409. */
    review: (recordId, body) => request(`/compliance/records/${encodeURIComponent(recordId)}/review`, {
        method: 'POST',
        body,
    }),
    archive: (recordId, body) => request(`/compliance/records/${encodeURIComponent(recordId)}/archive`, {
        method: 'POST',
        body,
    }),
    /** Dry run: resolves subjects, duplicates and expiry; writes nothing. */
    previewBatch: (payload) => request('/compliance/batches/preview', { method: 'POST', body: { payload } }),
    /**
     * Commit a previewed batch. The Idempotency-Key makes a retry replay the
     * first answer instead of writing twice; reuse the same key for a retry of
     * the same request and a new key for a new request.
     */
    commitBatch: (input, idempotencyKey) => {
        const form = new FormData();
        form.append('manifest', JSON.stringify({ payload: input.payload, plan_hash: input.planHash }));
        for (const part of input.files) {
            if (part.filename)
                form.append(`file:${part.ref}`, part.data, part.filename);
            else
                form.append(`file:${part.ref}`, part.data);
        }
        return request('/compliance/batches', {
            method: 'POST',
            body: form,
            headers: { 'Idempotency-Key': idempotencyKey },
            timeoutMs: COMPLIANCE_UPLOAD_TIMEOUT_MS,
        });
    },
    /** The server returns every readable company's contacts; this keeps one company's. */
    contacts: async (businessUnitId) => {
        const data = await request('/compliance/contacts');
        return asArray(data?.contacts).filter((c) => c.business_unit_id === businessUnitId);
    },
    /** People an admin of the company may add as contacts (admins only). */
    contactCandidates: async (businessUnitId) => {
        const data = await request(`/compliance/contacts/candidates?business_unit_id=${encodeURIComponent(businessUnitId)}`);
        return asArray(data?.candidates);
    },
    setContact: (body) => request('/compliance/contacts', { method: 'PUT', body }),
    /** Removal rides on the same PUT (`remove: true`). */
    removeContact: (body) => request('/compliance/contacts', { method: 'PUT', body: { ...body, remove: true } }),
    /** One company's settings; the server answers with every readable company. */
    settings: async (businessUnitId) => {
        const data = await request('/compliance/settings');
        const row = asArray(data?.settings).find((r) => r.business_unit_id === businessUnitId);
        return {
            business_unit_id: businessUnitId,
            additional_alert_emails: Array.isArray(row?.additional_alert_emails) ? row.additional_alert_emails : [],
        };
    },
    setSettings: (body) => request('/compliance/settings', { method: 'PUT', body }),
    /** Self-service: own records, own and standby trucks, check-out candidates. */
    myDocuments: () => request('/me/documents'),
};
/** Company vehicles. Edits and assignments need can_manage_fleet. */
export const vehicles = {
    list: (params = {}) => {
        const qs = new URLSearchParams();
        if (params.businessUnitId)
            qs.set('business_unit_id', params.businessUnitId);
        if (params.status)
            qs.set('status', params.status);
        if (params.includeDisposed)
            qs.set('include_disposed', 'true');
        const suffix = qs.toString();
        return request(`/vehicles${suffix ? `?${suffix}` : ''}`);
    },
    get: (vehicleId) => request(`/vehicles/${encodeURIComponent(vehicleId)}`),
    create: (fields) => request('/vehicles', { method: 'POST', body: { fields } }),
    /** A stale `expected_revision` is a 409. */
    update: (vehicleId, expectedRevision, fields) => request(`/vehicles/${encodeURIComponent(vehicleId)}`, {
        method: 'PUT',
        body: { expected_revision: expectedRevision, fields },
    }),
    assign: (vehicleId, body) => request(`/vehicles/${encodeURIComponent(vehicleId)}/assignments`, {
        method: 'POST',
        body,
    }),
    endAssignment: (assignmentId, body = {}) => request(`/vehicles/assignments/${encodeURIComponent(assignmentId)}/end`, {
        method: 'POST',
        body,
    }),
    /** Tech self check-out of a standby truck (expected return within 30 days). */
    checkout: (vehicleId, body) => request(`/vehicles/${encodeURIComponent(vehicleId)}/checkout`, {
        method: 'POST',
        body,
    }),
    /** Closes only the caller's own open temporary. */
    returnCheckout: (assignmentId) => request(`/vehicles/assignments/${encodeURIComponent(assignmentId)}/return`, {
        method: 'POST',
        body: {},
    }),
};
//# sourceMappingURL=methods.js.map